import { useState } from 'react';
import { Monitor, User, Globe, Server, Cpu } from 'lucide-react';

export interface GraphNode {
  id: string;
  type: 'host' | 'ip' | 'user' | 'process' | 'device';
  label: string;
  external?: boolean;
  alertCount: number;
}

export interface GraphEdge {
  from: string;
  to: string;
  count: number;
  label?: string;
  // Domini/URL osservati negli alert di questa relazione (backend services/entityGraph.ts):
  // visibili passando sul contatore dell'arco, senza aprire gli alert uno a uno.
  indicators?: { value: string; count: number }[];
  indicatorsHidden?: number;
}

const NODE_ICON = { host: Monitor, ip: Globe, user: User, process: Cpu, device: Server };
const NODE_COLOR: Record<GraphNode['type'], string> = {
  host: 'text-sky-300', ip: 'text-amber-300', user: 'text-violet-300',
  process: 'text-orange-300', device: 'text-emerald-300',
};

// Glifi usati dentro i nodi SVG (le icone React non sono inseribili direttamente nell'<svg>).
const NODE_GLYPH: Record<GraphNode['type'], string> = {
  host: '▣', ip: '⬢', user: '☻', process: '⚙', device: '▤',
};
const NODE_GLYPH_COLOR: Record<GraphNode['type'], string> = {
  host: '#7dd3fc', ip: '#fcd34d', user: '#c4b5fd', process: '#fdba74', device: '#6ee7b7',
};

const WIDTH = 640;
const HEIGHT = 380;

// Layout radiale: l'host colpito al centro, le altre entità distribuite su un anello.
// Sufficiente per il numero di entità di un singolo incident (tipicamente < 15).
function layout(nodes: GraphNode[]) {
  // Il backend mette per primo il nodo centrale (host colpito o, per un case di sola identità,
  // l'utente — vedi services/entityGraph.ts): non si cerca più per tipo.
  const root = nodes[0];
  const others = nodes.filter(n => n.id !== root?.id);
  const positions = new Map<string, { x: number; y: number }>();
  positions.set(root.id, { x: WIDTH / 2, y: HEIGHT / 2 });
  const radius = Math.min(WIDTH, HEIGHT) / 2 - 60;
  others.forEach((n, i) => {
    const angle = (2 * Math.PI * i) / others.length - Math.PI / 2;
    positions.set(n.id, { x: WIDTH / 2 + radius * Math.cos(angle), y: HEIGHT / 2 + radius * Math.sin(angle) });
  });
  return positions;
}

export default function EntityGraph({ nodes, edges }: { nodes: GraphNode[]; edges: GraphEdge[] }) {
  if (nodes.length <= 1) {
    return (
      <div className="rounded-lg bg-slate-900 h-64 flex items-center justify-center text-slate-400 text-sm">
        No structured entities extracted from the raw events of this incident.
      </div>
    );
  }

  const positions = layout(nodes);

  // Arco di cui mostrare il tooltip dei domini. Passaggio del mouse sul contatore, oppure click
  // (per touch e per lasciarlo aperto mentre si copia un dominio).
  const [openEdge, setOpenEdge] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);
  const midpoint = (e: GraphEdge) => {
    const from = positions.get(e.from);
    const to = positions.get(e.to);
    return from && to ? { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 } : null;
  };
  const tooltipEdge = openEdge !== null ? edges[openEdge] : null;
  const tooltipPos = tooltipEdge ? midpoint(tooltipEdge) : null;

  return (
    <div className="rounded-lg bg-slate-900 overflow-hidden">
      <div className="relative">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full" onClick={() => { if (pinned) { setPinned(false); setOpenEdge(null); } }}>
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
        </defs>

        {edges.map((e, i) => {
          const from = positions.get(e.from);
          const to = positions.get(e.to);
          if (!from || !to) return null;
          const mx = (from.x + to.x) / 2;
          const my = (from.y + to.y) / 2;
          const hasIndicators = !!e.indicators?.length;
          const active = openEdge === i;
          return (
            <g key={i}>
              <line x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                stroke="#475569" strokeWidth={1} strokeDasharray="4 3" markerEnd="url(#arrow)" />
              {/* Il contatore con dati dietro ha il bordo di accento: un invito visibile, non un
                  segreto da scoprire col mouse. Il bersaglio (r=14) è più largo del cerchio (r=8). */}
              <circle cx={mx} cy={my} r={8} fill={active ? '#0c4a6e' : '#1e293b'}
                stroke={hasIndicators ? '#38bdf8' : '#475569'} strokeWidth={hasIndicators ? 1.5 : 1} />
              <text x={mx} y={my + 3} textAnchor="middle" fontSize="9" fill="#cbd5e1">{e.count}</text>
              {e.label && (
                <text x={mx} y={my - 12} textAnchor="middle" fontSize="8" fill="#64748b">{e.label}</text>
              )}
              {hasIndicators && (
                <circle cx={mx} cy={my} r={14} fill="transparent" style={{ cursor: 'pointer' }}
                  onMouseEnter={() => { if (!pinned) setOpenEdge(i); }}
                  onMouseLeave={() => { if (!pinned) setOpenEdge(null); }}
                  onClick={ev => { ev.stopPropagation(); setOpenEdge(i); setPinned(p => !(p && openEdge === i)); }} />
              )}
            </g>
          );
        })}

        {nodes.map(n => {
          const pos = positions.get(n.id)!;
          const isRoot = n.id === nodes[0]?.id;
          return (
            <g key={n.id}>
              <circle cx={pos.x} cy={pos.y} r={isRoot ? 22 : 16}
                fill={isRoot ? '#0c4a6e' : '#1e293b'}
                stroke={n.external ? '#f87171' : '#475569'} strokeWidth={n.external ? 2 : 1} />
              <text x={pos.x} y={pos.y + 4} textAnchor="middle" fontSize={isRoot ? 12 : 10}
                fill={NODE_GLYPH_COLOR[n.type]} fontWeight="600">
                {NODE_GLYPH[n.type]}
              </text>
              <text x={pos.x} y={pos.y + (isRoot ? 38 : 30)} textAnchor="middle" fontSize="10" fill="#e2e8f0">
                {n.label.length > 26 ? `…${n.label.slice(-24)}` : n.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip in HTML sopra l'SVG: posizionato in percentuale del viewBox, così segue il
          ridimensionamento. Di fianco al contatore, non sotto: gli archi puntano tutti verso
          l'host al centro, e un pannello "sotto" finirebbe proprio sopra il nodo centrale. */}
      {tooltipEdge?.indicators && tooltipPos && (
        <div className="absolute z-10 pointer-events-none"
          style={{ left: `${(tooltipPos.x / WIDTH) * 100}%`, top: `${(tooltipPos.y / HEIGHT) * 100}%`,
            transform: tooltipPos.x > WIDTH * 0.6 ? 'translate(calc(-100% - 16px), -50%)' : 'translate(16px, -50%)' }}>
          <div className="rounded-md border border-slate-600 bg-slate-800/95 shadow-xl px-3 py-2 min-w-[220px] max-w-[320px] text-xs">
            <div className="text-slate-400 mb-1.5">
              {/* Domini per un URL-filtering EDR, firme IDS per un NDR: "cosa" è passato su questo arco. */}
              {tooltipEdge.indicators.length + (tooltipEdge.indicatorsHidden || 0)} indicator(s) in {tooltipEdge.count} alert(s)
            </div>
            <ul className="space-y-0.5">
              {tooltipEdge.indicators.map(d => (
                <li key={d.value} className="flex items-baseline justify-between gap-3">
                  <span className="font-mono text-slate-100 break-all">{d.value}</span>
                  <span className="text-slate-400 tabular-nums shrink-0">×{d.count}</span>
                </li>
              ))}
            </ul>
            {tooltipEdge.indicatorsHidden ? (
              <div className="text-slate-500 mt-1.5">+{tooltipEdge.indicatorsHidden} more — see the alert list</div>
            ) : null}
            {pinned && <div className="text-slate-500 mt-1.5">click elsewhere to close</div>}
          </div>
        </div>
      )}
      </div>

      <div className="flex flex-wrap gap-4 px-4 py-2 border-t border-slate-800 text-xs text-slate-400">
        {(Object.keys(NODE_ICON) as GraphNode['type'][]).map(type => {
          const Icon = NODE_ICON[type];
          const count = nodes.filter(n => n.type === type).length;
          if (count === 0) return null;
          return (
            <span key={type} className="flex items-center gap-1">
              <Icon size={12} className={NODE_COLOR[type]} />
              {type} ({count})
            </span>
          );
        })}
        <span className="flex items-center gap-1 ml-auto">
          <span className="w-2 h-2 rounded-full border-2 border-red-400" /> external entity
        </span>
      </div>
    </div>
  );
}
